import {AbstractRpcTransmitter, RpcCallableNode, RpcOp} from "./rpc.js";

export interface IRpcSessionFactory<SessionType extends RpcSession> {
    makeSession(overridePrimaryName?: string): SessionType;
}

export class RpcSessionFactory<SessionType extends RpcSession> implements IRpcSessionFactory<SessionType> {
    private nextConnId = 0;
    constructor(private clz: { new (connId: number, overridePrimaryName?: string): SessionType }) {}
    makeSession(overridePrimaryName?: string): SessionType {
        return new this.clz(++this.nextConnId, overridePrimaryName);
    }
}
export abstract class RpcSession {
    readonly primaryName: string;
    readonly names = new Set<string>();
    constructor(public readonly connId: number, overridePrimaryName?: string) {
        this.primaryName = overridePrimaryName ?? (':' + this.connId);
        this.registerName(null, this.primaryName, false);
    }
    abstract publicKey: string | null;
    abstract getHelloData(): any;
    abstract clientHelloReceived(publicKey: string, clientData: any): void;
    abstract isAllowedDestination(destNodeId: string, destObjectId: string): boolean;
    hasName(name: string) {
        return this.names.has(name);
    }
    registerName(namespace: string | null, peerName: string, validateName: boolean) {
        if (validateName && !/^[a-zA-Z_-][a-zA-Z0-9._-]*$/.test(peerName)) throw "invalid name";
        if (namespace) {
            this.names.add('::' + namespace + ':' + peerName);
        } else {
            this.names.add(peerName);
        }
    }
    getNames(): string[] {
        return [...this.names];
    }
}
export type RpcNamedTarget = RpcSession;

export type ConnHandler<Ctx> = AbstractRpcTransmitter<Ctx> & { session: RpcNamedTarget, clientPublicKey?: Uint8Array };

@AbstractRpcTransmitter.Mixin
export class RpcBroker<Ctx extends { session: RpcSession }> extends AbstractRpcTransmitter<Ctx> {
    public readonly peers : {[key: string]: ConnHandler<Ctx>} = {};

    constructor() {
        super();
        /*
        const self = this;
        const brokerCommands : any = {
            async debug_get_subscriptions(peer: Ctx) {
                return peer.session.subscriptions;
            },
            async debug_get_peers(peer: Ctx) {
                return Object.values(self.peers).map(client => client.session.getNames().filter(x => peer.session.isAllowedDestination(x))).filter(x => x.length !== 0);
            },
            async register_name(peer: Ctx, nameOrToken: string) {
                try {
                    if (typeof nameOrToken !== 'string') throw "invalid parameter type";
                    if (nameOrToken.startsWith('global_token_')) {
                        const setting = documents[ADMIN_USER + '/settings']?.['name-tokens'];
                        if (!setting[nameOrToken]) throw "not allowed";
                        const peerName = setting[nameOrToken].split(':');
                        if (peerName.length !== 4 || (peerName[2] !== 'global' && peerName[2] !== 'public')) throw "invalid name configured";
                        return peer.session.registerName(peerName[2], peerName[3], true);
                    }
                    let m;
                    if (m = nameOrToken.match(/^user_token_([a-z0-9]{2,16})_([a-z0-9]{32,})$/)) {
                        const setting = documents[m[1] + '/settings']?.['name-tokens'];
                        if (!setting[nameOrToken]) throw "not allowed";
                        const name = setting[nameOrToken];
                        return peer.session.registerName('user:'+m[1], name, true);
                    }
                    if (peer.session.loginUser) {
                        return peer.session.registerName('user:'+peer.session.loginUser?.username, nameOrToken, true);
                    }
                    if (peer.sender.session.publicKey) {
                        if (m = nameOrToken.match(/^::user:([a-z0-9]{2,16}):(.*)$/)) {
                            const setting = documents[m[1] + '/settings']?.['name-keys'];
                            if (!Object.entries(setting).some(([k,v]) => k.startsWith('allow') && v === peer.sender.session.publicKey+" "+nameOrToken)) throw "not allowed";
                            return peer.session.registerName(m[1], m[2], true);
                        } else {
                            const setting = documents[ADMIN_USER + '/settings']?.['name-tokens'];
                            if (!Object.entries(setting).some(([k,v]) => k.startsWith('allow') && v === peer.sender.session.publicKey+" "+nameOrToken)) throw "not allowed";
                            return peer.session.registerName(null, nameOrToken, false);
                        }
                    }
                    throw "invalid token format or name not allowed";
                } catch(ex) {
                    rpcServer.broadcastMessage('admin_message', 'register_name failed ('+nameOrToken+')', {node: peer.sender.session.primaryName, name: nameOrToken, pk: peer.sender.session.publicKey})
                    throw ex;
                }
            },
        };*/
    }

    findHandlersByName(name: string): ConnHandler<Ctx>[] {
        if (name in this.peers) return [this.peers[name]];
        return Object.values(this.peers).filter(h => h.session.hasName(name));
    }

    addHandler(handler: ConnHandler<Ctx>) {
        this.peers[handler.session.primaryName] = handler;
    }
    removeHandler(handler: ConnHandler<Ctx>) {
        delete this.peers[handler.session.primaryName];
    }

    sendRpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: Ctx, timeout?: number | undefined): Promise<any> | AsyncIterator<any> {
        if (context.session.isAllowedDestination(nodeId, objectId)) {
            const nodeIdParts = nodeId.split('/', 2);
            const handler = this.findHandlersByName(nodeIdParts[0]);
            if (handler.length == 0) throw "CALL dest not found";
            if (handler.length > 1) console.warn("multiple recipients for "+nodeId+": calling first");
            return handler[0].sendRpcCall(opCode, nodeIdParts.slice(1).join('/'), objectId, interfaceId, method, args, context, timeout);
        } else {
            throw "Call dest not allowed";
        }
    }
    sendRpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: Ctx) {
        if (context.session.isAllowedDestination(nodeId, objectId)) {
            const nodeIdParts = nodeId.split('/', 2);
            const peers = this.findHandlersByName(nodeIdParts[0]);
            for(const handler of peers) handler.sendRpcCallNoResponse(opCode, nodeIdParts.slice(1).join('/'), objectId, interfaceId, method, args, context);
        } else {
            throw "CallNoResponse dest not allowed";
        }
    }

}

export class RpcDummyBroker<Ctx> extends AbstractRpcTransmitter<Ctx> {
    constructor(private handler: RpcCallableNode<Ctx>) {
        super();
    }
    sendRpcCall(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: Ctx, timeout?: number): Promise<any> | AsyncIterator<any> {
        if (nodeId)
            throw new Error("No forwarding supported.");
        return this.handler.executeRpcCall(opCode, objectId, interfaceId, method, args, context, timeout);
    }
    sendRpcCallNoResponse(opCode: RpcOp, nodeId: string, objectId: string, interfaceId: string, method: string, args: any[], context: Ctx) {
        if (nodeId)
            throw new Error("No forwarding supported.");
        return this.handler.executeRpcCallNoResponse(opCode, objectId, interfaceId, method, args, context);
    }
}

/*
class RpcBrokerCommands<Ctx extends { session: RpcSession }> {
    async debug_get_subscriptions(peer: Ctx) {
        return peer.session.subscriptions;
    }
    async debug_get_peers(peer: Ctx) {
        return Object.values(broker.peers).map(client => client.session.getNames().filter(x => peer.session.isAllowedDestination(x))).filter(x => x.length !== 0);
    }
    async register_name(peer: Ctx, nameOrToken: string) {
        try {
            if (typeof nameOrToken !== 'string') throw "invalid parameter type";
            if (nameOrToken.startsWith('global_token_')) {
                const setting = documents[ADMIN_USER + '/settings']?.['name-tokens'];
                if (!setting[nameOrToken]) throw "not allowed";
                const peerName = setting[nameOrToken].split(':');
                if (peerName.length !== 4 || (peerName[2] !== 'global' && peerName[2] !== 'public')) throw "invalid name configured";
                return peer.session.registerName(peerName[2], peerName[3], true);
            }
            let m;
            if (m = nameOrToken.match(/^user_token_([a-z0-9]{2,16})_([a-z0-9]{32,})$/)) {
                const setting = documents[m[1] + '/settings']?.['name-tokens'];
                if (!setting[nameOrToken]) throw "not allowed";
                const name = setting[nameOrToken];
                return peer.session.registerName('user:'+m[1], name, true);
            }
            if (peer.session.loginUser) {
                return peer.session.registerName('user:'+peer.session.loginUser?.username, nameOrToken, true);
            }
            if (peer.sender.session.publicKey) {
                if (m = nameOrToken.match(/^::user:([a-z0-9]{2,16}):(.*)$/)) {
                    const setting = documents[m[1] + '/settings']?.['name-keys'];
                    if (!Object.entries(setting).some(([k,v]) => k.startsWith('allow') && v === peer.sender.session.publicKey+" "+nameOrToken)) throw "not allowed";
                    return peer.session.registerName(m[1], m[2], true);
                } else {
                    const setting = documents[ADMIN_USER + '/settings']?.['name-tokens'];
                    if (!Object.entries(setting).some(([k,v]) => k.startsWith('allow') && v === peer.sender.session.publicKey+" "+nameOrToken)) throw "not allowed";
                    return peer.session.registerName(null, nameOrToken, false);
                }
            }
            throw "invalid token format or name not allowed";
        } catch(ex) {
            rpcServer.broadcastMessage('admin_message', 'register_name failed ('+nameOrToken+')', {node: peer.sender.session.primaryName, name: nameOrToken, pk: peer.sender.session.publicKey})
            throw ex;
        }
    }
};
*/